// @tennis-matches/services/winterdoppel/players

import { getSupabaseClient } from "../supabase/index.js";
import { assertWinterDoppelPlayerNameUnique } from "./playerDisplay.js";
import {
  assertWinterDoppelPlayerChangeAllowed,
  WINTERDOPPEL_PUBLISHED_PLAYER_CHANGE,
} from "./publishedChange.js";
import { getWinterDoppelSeries } from "./series.js";

function __asStr(v) {
  return String(v ?? "").trim();
}

const PLAYER_SELECT =
  "id,tenant_id,created_by,series_id,club_person_id,display_name,phone,email,requires_email_confirm,email_confirmed_at,interest_status,week_rule,play_level,participation_limit_type,participation_limit_value,edit_token,self_service_at,is_planner,sort_order,created_at,updated_at";

export const WINTERDOPPEL_DEFAULT_PLAY_LEVEL = 3;

export function normalizeWinterDoppelPlayLevel(value) {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n) || n < 1 || n > 5) {
    return WINTERDOPPEL_DEFAULT_PLAY_LEVEL;
  }
  return n;
}

function __normalizeParticipationLimit(type, value, interestStatus) {
  const interest = __asStr(interestStatus || "yes") || "yes";
  let limitType = __asStr(type || "none") || "none";
  let limitValue =
    value == null || value === "" ? null : Math.floor(Number(value) || 0);
  if (limitType === "none") {
    limitValue = null;
  } else if (limitValue == null || limitValue < 1) {
    throw new Error("winterdoppel.player.invalidParticipationLimit");
  }
  if (interest === "no") {
    limitType = "none";
    limitValue = null;
  }
  return {
    participation_limit_type: limitType,
    participation_limit_value: limitValue,
  };
}

const IN_QUERY_CHUNK = 80;

export async function loadWinterDoppelPlayers(seriesId) {
  const supabase = await getSupabaseClient();
  const { data, error } = await supabase
    .from("winterdoppel_players")
    .select(PLAYER_SELECT)
    .eq("series_id", __asStr(seriesId))
    .order("sort_order", { ascending: true })
    .order("display_name", { ascending: true });
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

/** Spieler für viele Serien in wenigen Roundtrips. */
export async function loadWinterDoppelPlayersForSeriesIds(seriesIds = []) {
  const ids = [
    ...new Set((seriesIds || []).map((id) => __asStr(id)).filter(Boolean)),
  ];
  if (!ids.length) return [];
  const parts = [];
  for (let i = 0; i < ids.length; i += IN_QUERY_CHUNK) {
    const chunk = ids.slice(i, i + IN_QUERY_CHUNK);
    parts.push(
      (async () => {
        const supabase = await getSupabaseClient();
        const { data, error } = await supabase
          .from("winterdoppel_players")
          .select(PLAYER_SELECT)
          .in("series_id", chunk)
          .order("sort_order", { ascending: true })
          .order("display_name", { ascending: true });
        if (error) throw error;
        return Array.isArray(data) ? data : [];
      })(),
    );
  }
  return (await Promise.all(parts)).flat();
}

export async function loadWinterDoppelPlayerCountsBySeries(seriesIds = []) {
  const ids = [
    ...new Set((seriesIds || []).map((id) => __asStr(id)).filter(Boolean)),
  ];
  /** @type {Map<string, number>} */
  const counts = new Map(ids.map((id) => [id, 0]));
  if (!ids.length) return counts;

  const supabase = await getSupabaseClient();
  const { data, error } = await supabase
    .from("winterdoppel_players")
    .select("series_id")
    .in("series_id", ids);
  if (error) throw error;
  for (const row of data || []) {
    const sid = __asStr(row?.series_id);
    if (!sid) continue;
    counts.set(sid, (counts.get(sid) || 0) + 1);
  }
  return counts;
}

export async function createWinterDoppelPlayer(input = {}) {
  const supabase = await getSupabaseClient();
  const clubPersonId =
    __asStr(input.club_person_id || input.clubPersonId) || null;
  const row = {
    series_id: __asStr(input.series_id || input.seriesId),
    display_name: __asStr(input.display_name || input.displayName),
    phone: __asStr(input.phone) || null,
    club_person_id: clubPersonId,
    interest_status:
      __asStr(input.interest_status || input.interestStatus || "yes") || "yes",
    week_rule: __asStr(input.week_rule || input.weekRule || "all") || "all",
    play_level: normalizeWinterDoppelPlayLevel(
      input.play_level ?? input.playLevel,
    ),
    sort_order: Number(input.sort_order ?? input.sortOrder) || 0,
    ...__normalizeParticipationLimit(
      input.participation_limit_type ?? input.participationLimitType,
      input.participation_limit_value ?? input.participationLimitValue,
      input.interest_status || input.interestStatus || "yes",
    ),
  };
  const tid = __asStr(input.tenant_id || input.tenantId);
  if (tid) row.tenant_id = tid;
  if (!row.series_id || !row.display_name) {
    throw new Error("winterdoppel.player.missingFields");
  }
  const series = await getWinterDoppelSeries(row.series_id);
  assertWinterDoppelPlayerChangeAllowed(
    series,
    WINTERDOPPEL_PUBLISHED_PLAYER_CHANGE.CREATE,
    { allowPublished: Boolean(input.allowPublished) },
  );
  const existing = await loadWinterDoppelPlayers(row.series_id);
  assertWinterDoppelPlayerNameUnique(existing, row.display_name);
  if (
    clubPersonId &&
    existing.some((p) => __asStr(p.club_person_id) === clubPersonId)
  ) {
    throw new Error("winterdoppel.player.clubPersonAlreadyInSeries");
  }
  const { data, error } = await supabase
    .from("winterdoppel_players")
    .insert(row)
    .select(PLAYER_SELECT)
    .single();
  if (error) throw error;
  return data;
}

export async function updateWinterDoppelPlayer(id, updates = {}) {
  const supabase = await getSupabaseClient();
  const playerId = __asStr(id);
  const { data: scope, error: scopeErr } = await supabase
    .from("winterdoppel_players")
    .select("series_id")
    .eq("id", playerId)
    .maybeSingle();
  if (scopeErr) throw scopeErr;
  if (!scope?.series_id) throw new Error("winterdoppel.plan.notFound");
  const series = await getWinterDoppelSeries(scope.series_id);
  assertWinterDoppelPlayerChangeAllowed(
    series,
    WINTERDOPPEL_PUBLISHED_PLAYER_CHANGE.UPDATE,
  );
  const patch = {};
  if (updates.display_name != null || updates.displayName != null) {
    patch.display_name = __asStr(updates.display_name ?? updates.displayName);
  }
  if (updates.phone != null) patch.phone = __asStr(updates.phone) || null;
  const nextInterest =
    updates.interest_status != null || updates.interestStatus != null
      ? __asStr(updates.interest_status ?? updates.interestStatus)
      : undefined;
  if (nextInterest != null) patch.interest_status = nextInterest;
  if (updates.week_rule != null || updates.weekRule != null) {
    patch.week_rule = __asStr(updates.week_rule ?? updates.weekRule);
  }
  if (updates.play_level != null || updates.playLevel != null) {
    patch.play_level = normalizeWinterDoppelPlayLevel(
      updates.play_level ?? updates.playLevel,
    );
  }
  if (
    updates.participation_limit_type != null ||
    updates.participationLimitType != null ||
    updates.participation_limit_value != null ||
    updates.participationLimitValue != null ||
    nextInterest != null
  ) {
    const { data: current, error: readErr } = await supabase
      .from("winterdoppel_players")
      .select(
        "interest_status,participation_limit_type,participation_limit_value",
      )
      .eq("id", __asStr(id))
      .maybeSingle();
    if (readErr) throw readErr;
    const limits = __normalizeParticipationLimit(
      updates.participation_limit_type ??
        updates.participationLimitType ??
        current?.participation_limit_type,
      updates.participation_limit_value ??
        updates.participationLimitValue ??
        current?.participation_limit_value,
      nextInterest ?? current?.interest_status,
    );
    patch.participation_limit_type = limits.participation_limit_type;
    patch.participation_limit_value = limits.participation_limit_value;
  }
  if (updates.sort_order != null || updates.sortOrder != null) {
    patch.sort_order = Number(updates.sort_order ?? updates.sortOrder) || 0;
  }
  if (patch.display_name != null) {
    const { data: current, error: readErr } = await supabase
      .from("winterdoppel_players")
      .select("series_id")
      .eq("id", __asStr(id))
      .maybeSingle();
    if (readErr) throw readErr;
    if (current?.series_id) {
      const existing = await loadWinterDoppelPlayers(current.series_id);
      assertWinterDoppelPlayerNameUnique(existing, patch.display_name, id);
    }
  }
  const { data, error } = await supabase
    .from("winterdoppel_players")
    .update(patch)
    .eq("id", playerId)
    .select(PLAYER_SELECT)
    .single();
  if (error) throw error;
  return data;
}

export async function deleteWinterDoppelPlayer(id, opts = {}) {
  const supabase = await getSupabaseClient();
  const playerId = __asStr(id);
  const { data: scope, error: scopeErr } = await supabase
    .from("winterdoppel_players")
    .select("series_id")
    .eq("id", playerId)
    .maybeSingle();
  if (scopeErr) throw scopeErr;
  if (!scope?.series_id) throw new Error("winterdoppel.plan.notFound");
  const series = await getWinterDoppelSeries(scope.series_id);
  assertWinterDoppelPlayerChangeAllowed(
    series,
    WINTERDOPPEL_PUBLISHED_PLAYER_CHANGE.DELETE,
    { allowPublished: Boolean(opts.allowPublished) },
  );
  const { error } = await supabase
    .from("winterdoppel_players")
    .delete()
    .eq("id", playerId);
  if (error) throw error;
  return true;
}

export async function loadWinterDoppelPlayerBlocks(playerIds = []) {
  const ids = (Array.isArray(playerIds) ? playerIds : [])
    .map(__asStr)
    .filter(Boolean);
  if (!ids.length) return [];
  const supabase = await getSupabaseClient();
  const { data, error } = await supabase
    .from("winterdoppel_player_blocks")
    .select(
      "id,tenant_id,player_id,start_date,end_date,reason,created_by,created_at",
    )
    .in("player_id", ids)
    .order("start_date", { ascending: true });
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

export async function replaceWinterDoppelPlayerBlocks(
  playerId,
  blocks = [],
  tenantId,
) {
  const supabase = await getSupabaseClient();
  const pid = __asStr(playerId);
  const { data: scope, error: scopeErr } = await supabase
    .from("winterdoppel_players")
    .select("series_id")
    .eq("id", pid)
    .maybeSingle();
  if (scopeErr) throw scopeErr;
  if (!scope?.series_id) throw new Error("winterdoppel.plan.notFound");
  const series = await getWinterDoppelSeries(scope.series_id);
  assertWinterDoppelPlayerChangeAllowed(
    series,
    WINTERDOPPEL_PUBLISHED_PLAYER_CHANGE.BLOCKS,
  );
  const { error: delErr } = await supabase
    .from("winterdoppel_player_blocks")
    .delete()
    .eq("player_id", pid);
  if (delErr) throw delErr;

  const rows = (Array.isArray(blocks) ? blocks : [])
    .map((b) => {
      const start = __asStr(b.start_date || b.startDate);
      const end = __asStr(b.end_date || b.endDate || start);
      if (!start) return null;
      const row = {
        player_id: pid,
        start_date: start,
        end_date: end || start,
        reason: __asStr(b.reason) || null,
      };
      const tid = __asStr(tenantId || b.tenant_id || b.tenantId);
      if (tid) row.tenant_id = tid;
      return row;
    })
    .filter(Boolean);

  if (!rows.length) return [];
  const { data, error } = await supabase
    .from("winterdoppel_player_blocks")
    .insert(rows)
    .select(
      "id,tenant_id,player_id,start_date,end_date,reason,created_by,created_at",
    );
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}
